import { ReactElement, SyntheticEvent, useMemo, useCallback } from 'react';
import { Stack } from '@mui/material';
import {
  DoneOutlined as DoneIcon,
  CloseOutlined as CancelIcon,
} from '@mui/icons-material';
import { Chart as ChartJS, ArcElement } from 'chart.js';
import { Doughnut as DoughnutChart } from 'react-chartjs-2';

import currentTheme from '~config/theme';
import { toAbsoluteUrl } from '~utils/assetsHelpers';
import { pluralize } from '~utils/commons';
import { ORDERS_FINALIZED_STATE_OPTIONS } from '~constants/ordersStatesOptions';

import { useDashboardVariantV2CarrierContext } from '../../../Context';
// eslint-disable-next-line max-len
import {
  useDashboardVariantV2CarrierFinalizedFiltersContext,
  DEFAULT_FILTERS_STATE,
} from '../../../FinalizedFiltersContext';
import { useDashboardVariantV2CarrierPresetsContext } from '../../../PresetsContext';
import { DashboardVariantV2CarrierContextTabId } from '../../../types';
import {
  DashboardVariantV2CarrierStatusInfoWidgetCard,
  DashboardVariantV2CarrierStatusItemsList,
  DashboardVariantV2CarrierHeaderCaptionText,
} from '../../SubComponents';

import { getChartProps } from './utils';

ChartJS.register(ArcElement);

const WIDGET_TAB_ID: DashboardVariantV2CarrierContextTabId = 'finalized';
const WIDGET_ACCENT_COLOR = currentTheme.palette.success.main;

const [FULLFILLED_STATE_OPTION, ...WITH_INCIDENTS_STATE_OPTIONS] =
  ORDERS_FINALIZED_STATE_OPTIONS;

const DashboardVariantV2CarrierFinalizedWidgetCard = (): ReactElement => {
  const {
    data: {
      statistics: {
        data: {
          totalItems,
          completed: {
            totalCompletedtems,
            totalWithoutIssuesItems,
            totalWithIssuesItems,
          },
        },
      },
    },
    activeTab: { getIsActiveTab, setActiveTab },
  } = useDashboardVariantV2CarrierContext();

  const {
    autocompletesRefs: { itemState: itemStateAutocompleteRef },
    filters: { handleClearFilters, handleClearFilterByValues },
    forceRequest: { toggleValue: toggleForceRequest },
  } = useDashboardVariantV2CarrierFinalizedFiltersContext();

  const {
    selectedPreset: { reset: handleResetSelectedPreset },
  } = useDashboardVariantV2CarrierPresetsContext();

  const isSelected = useMemo(
    () => getIsActiveTab(WIDGET_TAB_ID),
    [getIsActiveTab],
  );

  const handleSelectedTab = useCallback(() => {
    if (!isSelected) {
      handleResetSelectedPreset();
    }

    setActiveTab(WIDGET_TAB_ID);
  }, [isSelected, setActiveTab, handleResetSelectedPreset]);

  const handleSelectedTabByFilters = useCallback(
    (values: Record<string, unknown>, callback?: () => void) =>
      (event: SyntheticEvent) => {
        event.stopPropagation();

        handleClearFilters(false);
        handleClearFilterByValues(values, () => toggleForceRequest(isSelected));
        handleResetSelectedPreset();

        setActiveTab(WIDGET_TAB_ID);

        if (callback) {
          setTimeout(() => {
            callback();
          }, 150);
        }
      },
    [
      handleClearFilterByValues,
      handleResetSelectedPreset,
      isSelected,
      setActiveTab,
      toggleForceRequest,
      handleClearFilters,
    ],
  );

  const handleSelectedTabWithFullFilledState = useCallback(
    (event: SyntheticEvent) => {
      handleSelectedTabByFilters(
        {
          itemState: [FULLFILLED_STATE_OPTION],
          finalizedDateTimeFrom: DEFAULT_FILTERS_STATE.finalizedDateTimeFrom
            ? new Date(DEFAULT_FILTERS_STATE.finalizedDateTimeFrom)
            : null,
          finalizedDateTimeTo: DEFAULT_FILTERS_STATE.finalizedDateTimeTo
            ? new Date(DEFAULT_FILTERS_STATE.finalizedDateTimeTo)
            : null,
        },
        () =>
          itemStateAutocompleteRef.current?.setValue([FULLFILLED_STATE_OPTION]),
      )(event);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [handleSelectedTabByFilters],
  );

  const handleSelectedTabWithIncidentsStates = useCallback(
    (event: SyntheticEvent) => {
      handleSelectedTabByFilters(
        {
          itemState: WITH_INCIDENTS_STATE_OPTIONS,
          finalizedDateTimeFrom: DEFAULT_FILTERS_STATE.finalizedDateTimeFrom
            ? new Date(DEFAULT_FILTERS_STATE.finalizedDateTimeFrom)
            : null,
          finalizedDateTimeTo: DEFAULT_FILTERS_STATE.finalizedDateTimeTo
            ? new Date(DEFAULT_FILTERS_STATE.finalizedDateTimeTo)
            : null,
        },
        () =>
          itemStateAutocompleteRef.current?.setValue(
            WITH_INCIDENTS_STATE_OPTIONS,
          ),
      )(event);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [handleSelectedTabByFilters],
  );

  const hasWithIssuesItems = useMemo(
    () => totalWithIssuesItems > 0,
    [totalWithIssuesItems],
  );

  return (
    <DashboardVariantV2CarrierStatusInfoWidgetCard
      accentColor={WIDGET_ACCENT_COLOR}
      isSelected={isSelected}
      onSelected={handleSelectedTab}
      value={{ current: totalCompletedtems, total: totalItems }}
      title={{
        text: 'FINALIZADOS',
        tooltip: 'Pedidos cumplidos con y sin incidencias.',
      }}
      icon={{
        src: toAbsoluteUrl('/icons/icon_section_completed.svg'),
        alt: 'Section Completed Icon',
      }}
      alert={{
        show: hasWithIssuesItems,
        tooltip: pluralize({
          singular: 'Con incidencia',
          plural: 'Con incidencias',
          count: totalWithIssuesItems,
          includeCount: true,
        }),
        severity: 'warning',
        onClick: handleSelectedTabWithIncidentsStates,
      }}
      content={
        <Stack spacing={2} direction="row">
          <DoughnutChart
            {...getChartProps(
              totalCompletedtems,
              totalWithoutIssuesItems,
              totalWithIssuesItems,
            )}
          />

          <Stack flex={1}>
            <DashboardVariantV2CarrierStatusItemsList
              items={[
                {
                  icon: <DoneIcon fontSize="small" color="success" />,
                  label: 'Cumplidos',
                  value: totalWithoutIssuesItems,
                  onClick: handleSelectedTabWithFullFilledState,
                },
                {
                  icon: <CancelIcon fontSize="small" color="error" />,
                  label: 'Con incidencias',
                  value: totalWithIssuesItems,
                  onClick: handleSelectedTabWithIncidentsStates,
                },
              ]}
            />
          </Stack>
        </Stack>
      }
      footer={
        <DashboardVariantV2CarrierHeaderCaptionText color={WIDGET_ACCENT_COLOR}>
          Ver pedidos finalizados
        </DashboardVariantV2CarrierHeaderCaptionText>
      }
    />
  );
};

export default DashboardVariantV2CarrierFinalizedWidgetCard;
